import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Client,
  ClientRateCardInput,
  ClientRateCardTierInput,
  RateCardTierType,
} from '@core/generated/gql-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';

const CENTS_PER_DOLLAR = 100;

type TierRow = {
  id: string;
  minDays: string;
  maxDays: string;
  type: RateCardTierType;
  amountDollars: string;
  label: string;
};

const createEmptyTierRow = (): TierRow => ({
  id: `tier-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  minDays: '1',
  maxDays: '',
  type: RateCardTierType.Flat,
  amountDollars: '',
  label: '',
});

const rateCardToTierRows = (
  rateCard: NonNullable<Client['rateCard']>,
): TierRow[] => {
  return (rateCard.tiers ?? []).map((tier, index) => ({
    id: `tier-${tier.minDays}-${index}`,
    minDays: String(tier.minDays),
    maxDays: tier.maxDays != null ? String(tier.maxDays) : '',
    type: tier.type,
    amountDollars:
      tier.type === RateCardTierType.PerDay
        ? tier.perDayCents != null
          ? (tier.perDayCents / 100).toFixed(2)
          : ''
        : tier.flatCents != null
          ? (tier.flatCents / 100).toFixed(2)
          : '',
    label: tier.label ?? '',
  }));
};

export interface RateCardRatesProps {
  client: Client;
  disabled?: boolean;
  onSave: (
    rateCard: ClientRateCardInput,
  ) => Promise<{ success: boolean; error?: string }>;
}

export const RateCardRates = ({
  client,
  disabled,
  onSave,
}: RateCardRatesProps) => {
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [currency, setCurrency] = useState('AUD');
  const [bookingFeeDollars, setBookingFeeDollars] = useState('');
  const [tierRows, setTierRows] = useState<TierRow[]>([createEmptyTierRow()]);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const rateCard = client.effectiveRateCard ?? client.rateCard;
    if (rateCard) {
      setEffectiveFrom(rateCard.effectiveFrom ?? '');
      setCurrency(rateCard.currency ?? 'AUD');
      setBookingFeeDollars(
        rateCard.bookingFeeCents != null
          ? (rateCard.bookingFeeCents / CENTS_PER_DOLLAR).toFixed(2)
          : '',
      );
      setTierRows(
        rateCard.tiers?.length
          ? rateCardToTierRows(rateCard)
          : [createEmptyTierRow()],
      );
    } else {
      setEffectiveFrom('');
      setCurrency('AUD');
      setBookingFeeDollars('');
      setTierRows([createEmptyTierRow()]);
    }
  }, [client.id, client.effectiveRateCard, client.rateCard]);

  const addTierRow = () => {
    setTierRows((previous) => [...previous, createEmptyTierRow()]);
  };

  const removeTierRow = (rowId: string) => {
    setTierRows((previous) =>
      previous.length <= 1
        ? previous
        : previous.filter((row) => row.id !== rowId),
    );
  };

  const updateTierRow = (rowId: string, patch: Partial<TierRow>) => {
    setTierRows((previous) =>
      previous.map((row) => (row.id === rowId ? { ...row, ...patch } : row)),
    );
  };

  const clearBookingFee = () => {
    setBookingFeeDollars('');
  };

  const handleSave = async () => {
    setFormError('');
    if (!effectiveFrom.trim()) {
      setFormError('Effective from date is required');
      return;
    }

    const tiers: ClientRateCardTierInput[] = [];
    for (const row of tierRows) {
      const minDays = parseInt(row.minDays, 10);
      if (Number.isNaN(minDays) || minDays < 1) {
        setFormError('Each tier needs a minimum day of at least 1');
        return;
      }

      let maxDays: number | undefined;
      if (row.maxDays.trim() !== '') {
        maxDays = parseInt(row.maxDays, 10);
        if (Number.isNaN(maxDays) || maxDays < minDays) {
          setFormError(
            'Maximum days must be empty (open-ended) or greater than or equal to minimum days',
          );
          return;
        }
      }

      const dollars = parseFloat(row.amountDollars.trim());
      if (Number.isNaN(dollars) || dollars < 0) {
        setFormError('Each tier needs a valid amount in dollars');
        return;
      }

      const cents = Math.round(dollars * 100);
      const tierInput: ClientRateCardTierInput = {
        minDays,
        type: row.type,
        label: row.label.trim() || undefined,
      };
      if (maxDays !== undefined) {
        tierInput.maxDays = maxDays;
      }
      if (row.type === RateCardTierType.PerDay) {
        tierInput.perDayCents = cents;
      } else {
        tierInput.flatCents = cents;
      }
      tiers.push(tierInput);
    }

    const trimmedBookingFee = bookingFeeDollars.trim();
    let bookingFeeCents: number | null = null;
    if (trimmedBookingFee !== '') {
      const bookingFeeAmount = parseFloat(trimmedBookingFee);
      if (Number.isNaN(bookingFeeAmount) || bookingFeeAmount < 0) {
        setFormError('Booking fee must be a valid amount in dollars');
        return;
      }
      bookingFeeCents = Math.round(bookingFeeAmount * CENTS_PER_DOLLAR);
    }

    const rateCardPayload: ClientRateCardInput = {
      effectiveFrom: effectiveFrom.trim(),
      currency: currency.trim() || 'AUD',
      tiers,
      bookingFeeCents,
    };

    setSaving(true);
    const result = await onSave(rateCardPayload);
    setSaving(false);

    if (result.success) {
      toast.success('Rate card saved');
    } else if (result.error) {
      setFormError(result.error);
      toast.error(result.error);
    }
  };

  return (
    <div className="space-y-4">
      {formError ? (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
          {formError}
        </div>
      ) : null}

      <div className="grid max-w-xl grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label
            className="mb-1 block text-sm font-medium text-gray-700"
            htmlFor="rate-card-effective-from"
          >
            Effective from
          </label>
          <input
            id="rate-card-effective-from"
            type="date"
            disabled={disabled || saving}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
            value={effectiveFrom}
            onChange={(event) => setEffectiveFrom(event.target.value)}
          />
        </div>
        <div>
          <label
            className="mb-1 block text-sm font-medium text-gray-700"
            htmlFor="rate-card-currency"
          >
            Currency
          </label>
          <input
            id="rate-card-currency"
            type="text"
            disabled={disabled || saving}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm uppercase disabled:bg-gray-50"
            value={currency}
            onChange={(event) => setCurrency(event.target.value.toUpperCase())}
            placeholder="AUD"
          />
        </div>
      </div>

      <div className="max-w-xl">
        <label
          className="mb-1 block text-sm font-medium text-gray-700"
          htmlFor="rate-card-booking-fee"
        >
          Booking fee ($)
        </label>
        <div className="flex items-center gap-2">
          <input
            id="rate-card-booking-fee"
            type="text"
            inputMode="decimal"
            disabled={disabled || saving}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
            value={bookingFeeDollars}
            onChange={(event) => setBookingFeeDollars(event.target.value)}
            placeholder="One-off job fee (optional)"
          />
          <button
            type="button"
            title="Remove booking fee"
            disabled={disabled || saving || bookingFeeDollars.trim() === ''}
            onClick={clearBookingFee}
            className="shrink-0 rounded p-2 text-gray-500 hover:bg-gray-100 hover:text-red-600 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Optional one-off fee applied per job. Save with an empty value to
          remove an existing fee.
        </p>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          disabled={disabled || saving}
          onClick={addTierRow}
          className="mb-2 flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-sm hover:bg-gray-50 disabled:opacity-50"
        >
          <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
          Add tier
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-100 text-left">
              <th className="border-b border-gray-200 px-3 py-2 font-semibold">
                Min days
              </th>
              <th className="border-b border-gray-200 px-3 py-2 font-semibold">
                Max days
              </th>
              <th className="border-b border-gray-200 px-3 py-2 font-semibold">
                Type
              </th>
              <th className="border-b border-gray-200 px-3 py-2 font-semibold">
                Amount ($)
              </th>
              <th className="border-b border-gray-200 px-3 py-2 font-semibold">
                Label
              </th>
              <th className="w-16 border-b border-gray-200 px-3 py-2">
                <span className="sr-only">Remove tier</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {tierRows.map((row) => (
              <tr key={row.id} className="border-b border-gray-100">
                <td className="px-3 py-2 align-top">
                  <input
                    type="number"
                    min={1}
                    disabled={disabled || saving}
                    className="w-20 rounded border border-gray-300 px-2 py-1 disabled:bg-gray-50"
                    value={row.minDays}
                    onChange={(event) =>
                      updateTierRow(row.id, { minDays: event.target.value })
                    }
                  />
                </td>
                <td className="px-3 py-2 align-top">
                  <input
                    type="number"
                    min={1}
                    disabled={disabled || saving}
                    className="w-20 rounded border border-gray-300 px-2 py-1 disabled:bg-gray-50"
                    value={row.maxDays}
                    onChange={(event) =>
                      updateTierRow(row.id, { maxDays: event.target.value })
                    }
                    placeholder="—"
                  />
                </td>
                <td className="px-3 py-2 align-top">
                  <select
                    disabled={disabled || saving}
                    className="rounded border border-gray-300 px-2 py-1 disabled:bg-gray-50"
                    value={row.type}
                    onChange={(event) =>
                      updateTierRow(row.id, {
                        type: event.target.value as RateCardTierType,
                      })
                    }
                  >
                    <option value={RateCardTierType.Flat}>Flat</option>
                    <option value={RateCardTierType.PerDay}>Per day</option>
                  </select>
                </td>
                <td className="px-3 py-2 align-top">
                  <input
                    type="text"
                    inputMode="decimal"
                    disabled={disabled || saving}
                    className="w-28 rounded border border-gray-300 px-2 py-1 disabled:bg-gray-50"
                    value={row.amountDollars}
                    onChange={(event) =>
                      updateTierRow(row.id, {
                        amountDollars: event.target.value,
                      })
                    }
                    placeholder={
                      row.type === RateCardTierType.PerDay ? 'Per day' : 'Flat'
                    }
                  />
                </td>
                <td className="px-3 py-2 align-top">
                  <input
                    type="text"
                    disabled={disabled || saving}
                    className="w-full min-w-40 rounded border border-gray-300 px-2 py-1 disabled:bg-gray-50"
                    value={row.label}
                    onChange={(event) =>
                      updateTierRow(row.id, { label: event.target.value })
                    }
                    placeholder="Description"
                  />
                </td>
                <td className="px-3 py-2 align-top">
                  <button
                    type="button"
                    title="Remove tier"
                    disabled={disabled || saving || tierRows.length <= 1}
                    onClick={() => removeTierRow(row.id)}
                    className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-red-600 disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={disabled || saving}
          onClick={handleSave}
          className="rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save rate card'}
        </button>
        <p className="text-xs text-gray-500">
          Leave max days empty for an open-ended top tier. Use flat or per-day
          pricing per row.
        </p>
      </div>
    </div>
  );
};
